/**
 * Normalize a discount value to a percentage (0-100).
 * Supports both decimal (0.10) and percentage (10) inputs.
 */
export const normalizeDiscount = (discount) => {
  const value = parseFloat(discount);
  if (isNaN(value) || value < 0) return 0;
  if (value > 1 && value <= 100) return value;
  if (value <= 1) return value * 100;
  return 100;
};

/**
 * Calculate discount percentage from MRP and rate (selling price).
 */
export const getDiscountFromRate = (mrp, rate) => {
  const numericMrp = parseFloat(mrp) || 0;
  const numericRate = parseFloat(rate) || 0;
  if (numericMrp <= 0 || numericRate < 0 || numericRate > numericMrp) return 0;
  return parseFloat((((numericMrp - numericRate) / numericMrp) * 100).toFixed(2));
};

/**
 * Calculate rate (selling price) from MRP and discount percentage.
 */
export const getRateFromDiscount = (mrp, discountPercent) => {
  const numericMrp = parseFloat(mrp) || 0;
  const discount = normalizeDiscount(discountPercent);
  if (numericMrp <= 0) return 0;
  return Math.round(numericMrp * (1 - discount / 100));
};

/**
 * Determine whether an offer applies to a given size.
 * - offer.sizeId is null/undefined/0/'all' => applies to all sizes
 * - otherwise must match the provided sizeId
 */
export const offerAppliesToSize = (offer, sizeId) => {
  const offerSize = offer.sizeId;
  if (offerSize === null || offerSize === undefined || offerSize === '' || offerSize === 'all' || offerSize === 0) {
    return true;
  }
  return String(offerSize) === String(sizeId);
};

/**
 * Calculate the effective per-unit price for a product after applying the best
 * applicable quantity-based bulk offer.
 *
 * @param {number} basePrice - Product or size price (after any base discount)
 * @param {number} quantity - Quantity being purchased
 * @param {Array} offers - Array of { minQty, discountPercent, rate, label, sizeId }
 * @param {string|number} sizeId - Optional size identifier for size-specific offers
 * @returns {{price: number, offerApplied: object|null}}
 */
export const getEffectivePriceWithOffer = (basePrice, quantity, offers = [], sizeId = null) => {
  const numericBase = parseFloat(basePrice) || 0;
  const qty = parseInt(quantity) || 0;

  if (!offers || offers.length === 0 || qty <= 0 || numericBase <= 0) {
    return { price: numericBase, offerApplied: null };
  }

  // Filter applicable offers: minQty met and applies to this size
  const applicableOffers = offers
    .filter((offer) => parseInt(offer.minQty) <= qty && offerAppliesToSize(offer, sizeId))
    .map((offer) => {
      // Prefer explicit discountPercent; fall back to rate-derived discount
      const discount = parseFloat(offer.discountPercent);
      if (!isNaN(discount) && discount > 0) {
        return { ...offer, effectiveDiscount: normalizeDiscount(discount) };
      }
      const rate = parseFloat(offer.rate);
      if (!isNaN(rate) && rate > 0) {
        return { ...offer, effectiveDiscount: getDiscountFromRate(numericBase, rate) };
      }
      return { ...offer, effectiveDiscount: 0 };
    })
    .filter((offer) => offer.effectiveDiscount > 0)
    .sort((a, b) => b.effectiveDiscount - a.effectiveDiscount);

  const bestOffer = applicableOffers[0];

  if (!bestOffer) {
    return { price: numericBase, offerApplied: null };
  }

  const discountedPrice = numericBase * (1 - bestOffer.effectiveDiscount / 100);

  return {
    price: Math.max(0, discountedPrice),
    offerApplied: bestOffer,
  };
};

/**
 * Calculate line total for a cart item, considering per-size discount and bulk offers.
 */
export const getCartItemTotal = (item) => {
  const mrp = parseFloat(item.price) > 0 ? parseFloat(item.price) : parseFloat(item.product_base_price) || 0;
  const qty = parseInt(item.quantity) || 0;
  const offers = item.offers || [];
  const sizeId = item.sizeId || item.size_id || null;

  // Apply per-size/item discount first (discount stored as percentage 0-100)
  const itemDiscount = normalizeDiscount(item.discount);
  const priceAfterItemDiscount = Math.max(0, mrp * (1 - itemDiscount / 100));

  // Then apply quantity-based bulk offers on top of the already-discounted price
  const { price: effectivePrice, offerApplied } = getEffectivePriceWithOffer(
    priceAfterItemDiscount,
    qty,
    offers,
    sizeId
  );

  return {
    mrp: Math.round(mrp),
    unitPrice: Math.round(effectivePrice),
    total: Math.round(effectivePrice * qty),
    offerApplied,
    itemDiscount,
  };
};

/**
 * Build a human-readable label for an offer.
 */
export const getOfferLabel = (offer, sizeName = null) => {
  if (offer.label) return offer.label;
  const sizePart = sizeName ? ` on ${sizeName}` : '';
  if (offer.discountPercent) {
    return `Buy ${offer.minQty}+ get ${normalizeDiscount(offer.discountPercent)}% off${sizePart}`;
  }
  if (offer.rate) {
    return `Buy ${offer.minQty}+ at ₹${Math.round(offer.rate)}${sizePart}`;
  }
  return `Buy ${offer.minQty}+${sizePart}`;
};
